import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { AuthService } from 'src/app/auth.service';
import { UserDataService } from 'src/app/services/user-data-service';
import { CopilotService } from 'src/app/copilot/services/copilot.service';
import { AnalystFeedbackEntry, CopilotOrgSettings } from 'src/app/copilot/models/copilot.models';
import { TransformDefaultTypeUtil } from 'src/app/domainObjects/transformType';

@Component({
  selector: 'app-copilot-settings',
  templateUrl: './copilot-settings.component.html',
  standalone: false
})
export class CopilotSettingsComponent implements OnInit {
  feedbackEntries: AnalystFeedbackEntry[] = [];
  feedbackLoading = false;
  feedbackError = false;

  orgSettings: CopilotOrgSettings | null = null;
  orgSettingsLoading = false;
  orgSettingsError = false;
  orgSettingsSaving = false;

  @ViewChild('capSlider', { read: ElementRef }) capSliderRef?: ElementRef<HTMLElement>;
  @ViewChild('tickLabelRow', { read: ElementRef }) tickLabelRowRef?: ElementRef<HTMLElement>;

  /** Pixel offsets (left, relative to tickLabelRow) for each step's tick label —
   * measured directly from the slider's own rendered tick marks so labels always
   * land exactly under the real ticks, regardless of step count or label width.
   * A flex/space-between row can't reproduce this: Material spaces variable-width
   * text ("500" vs "10,000") by equal gaps between edges, not equal centers, which
   * drifted a label up to ~27px off its true tick — confirmed live, 2026-09-17. */
  tickLabelPositions: number[] = [];

  constructor(
    private authService: AuthService,
    private userData: UserDataService,
    private copilotSvc: CopilotService,
  ) {}

  ngOnInit(): void {
    if (this.isSuperAdmin()) {
      this.loadFeedback();
      this.loadOrgSettings();
    }
  }

  isSuperAdmin(): boolean {
    const user = this.authService.getUserDetails();
    return user !== null && user.userType === 1;
  }

  transformTypeLabel(transformType: number | null): string {
    return transformType !== null ? (TransformDefaultTypeUtil.toString(transformType) || `Type ${transformType}`) : '';
  }

  loadFeedback(): void {
    this.feedbackLoading = true;
    this.feedbackError = false;
    this.copilotSvc.getAnalystFeedback(1).subscribe({
      next: (res) => {
        this.feedbackEntries = res.entries ?? [];
        this.feedbackLoading = false;
      },
      error: () => {
        this.feedbackError = true;
        this.feedbackLoading = false;
      },
    });
  }

  // ------------------------------------------------------------------
  // Organization Settings (Super Admin) — org-wide enable kill switch and
  // the daily per-Analyst token cap. See app/copilot/copilot_org_settings_store.py.
  // ------------------------------------------------------------------

  /**
   * showLoading is true only for the genuine initial load (ngOnInit) — the
   * *ngIf="orgSettingsLoading" in the template hides the whole card while
   * true, which is right for "nothing to show yet" but was also firing after
   * every save (the toggle/slider handlers below used to always reload with
   * loading=true), making the whole section blank-then-reappear on every
   * change. A post-save refresh already has data on screen; it only needs to
   * silently pick up the server's response (e.g. the new pending-step note),
   * not hide the card while doing it.
   */
  loadOrgSettings(showLoading: boolean = true): void {
    if (showLoading) this.orgSettingsLoading = true;
    this.orgSettingsError = false;
    this.copilotSvc.getOrgCopilotSettings(this.userData.getOrgId(), 1).subscribe({
      next: (res) => {
        this.orgSettings = res;
        this.orgSettingsLoading = false;
        // Slider/label row are behind *ngIf — give Angular a tick to render them
        // (and Material to lay out its own tick marks) before measuring.
        setTimeout(() => this.measureTickLabelPositions());
      },
      error: () => {
        this.orgSettingsError = true;
        this.orgSettingsLoading = false;
      },
    });
  }

  /**
   * Reads the actual DOM position of each Material tick mark and stores the
   * matching x-offset for the label row below the slider. Must run after
   * Angular paints the slider — MDC creates its tick marks itself, at a
   * spacing derived from the slider's own live layout, so this can't be
   * replicated with static CSS percentages or flex spacing.
   */
  private measureTickLabelPositions(): void {
    const sliderEl = this.capSliderRef?.nativeElement;
    const rowEl = this.tickLabelRowRef?.nativeElement;
    if (!sliderEl || !rowEl) return;
    const ticks = Array.from(sliderEl.querySelectorAll<HTMLElement>(
      '.mdc-slider__tick-mark--active, .mdc-slider__tick-mark--inactive'
    ));
    if (!ticks.length) return;
    const rowRect = rowEl.getBoundingClientRect();
    this.tickLabelPositions = ticks.map(t => {
      const r = t.getBoundingClientRect();
      return (r.left + r.right) / 2 - rowRect.left;
    });
  }

  onOrgEnabledToggle(enabled: boolean): void {
    this.orgSettingsSaving = true;
    this.copilotSvc.setOrgCopilotEnabled(
      this.userData.getOrgId(), this.authService.getUserId() ?? '', 1, enabled,
    ).subscribe({
      next: () => { this.orgSettingsSaving = false; this.loadOrgSettings(false); },
      error: () => { this.orgSettingsSaving = false; this.orgSettingsError = true; },
    });
  }

  onCapStepChange(step: number): void {
    this.orgSettingsSaving = true;
    this.copilotSvc.setOrgCopilotCapStep(
      this.userData.getOrgId(), this.authService.getUserId() ?? '', 1, step,
    ).subscribe({
      next: () => { this.orgSettingsSaving = false; this.loadOrgSettings(false); },
      error: () => { this.orgSettingsSaving = false; this.orgSettingsError = true; },
    });
  }

  onQualityThresholdChange(pct: number): void {
    this.orgSettingsSaving = true;
    this.copilotSvc.setOrgCopilotQualityThreshold(
      this.userData.getOrgId(), this.authService.getUserId() ?? '', 1, pct,
    ).subscribe({
      next: () => { this.orgSettingsSaving = false; this.loadOrgSettings(false); },
      error: () => { this.orgSettingsSaving = false; this.orgSettingsError = true; },
    });
  }

  /** mat-slider's [displayWith] for the quality-threshold slider — the raw value
   * IS the percent already (90-99), so this just adds the "%" suffix. Arrow
   * function so `this` binds correctly when Material invokes it internally. */
  formatQualityPct = (pct: number): string => `${pct}%`;

  /** mat-slider's [displayWith] — shows the real credit value in the hover/drag
   * tooltip instead of the raw 1-10 step index. Arrow function (not a class
   * method) so `this` binds correctly when Material invokes it internally. */
  formatCapStepLabel = (step: number): string => {
    const credits = this.orgSettings?.steps?.[step - 1];
    return credits != null ? this.formatCredits(credits) : String(step);
  };

  /** "500"/"15,000" style formatting — generic for any credits/day value, not
   * tied to the current placeholder step table. Public: also used directly by
   * the always-visible tick labels under the slider, so both locations are
   * guaranteed to render identically (one shared function, not two). */
  formatCredits(credits: number): string {
    return credits.toLocaleString();
  }
}
